/**
 * The one confirmation every irreversible action goes through (XC-01, FR-E55).
 *
 * It says what the action does and does not do (the caller's `children`), then asks for a
 * fresh two-step code — unless an assertion from a moment ago is still in hand
 * (`lib/stepup.ts`) — trades it for a step-up assertion, and only then runs the action with
 * it. Confirming never closes the dialog on its own: a refusal stays on screen as a
 * sentence, and the caller closes it when its request succeeds.
 *
 * Used by the per-period filing panel (submit, proceed, file) and by the Filing screen
 * (request an OTP, open a session), which the service gates on the same step-up.
 */

import { Button, ConfirmDialog, Input, RadioGroup } from '@fiveox/ui'
import { type ReactNode, useEffect, useState } from 'react'

import { ApiError } from '../lib/api'
import {
  STEP_UP_CHOICES,
  type StepUpMethod,
  StepUpError,
  forgetAssertion,
  heldAssertion,
  obtainStepUp,
  sendStepUpCode,
} from '../lib/stepup'

export interface IrreversibleConfirmProps {
  open: boolean
  onClose: () => void
  title: string
  /** What confirming does, and what it does not. */
  children: ReactNode
  confirmLabel: string
  /** The action, given the assertion. Throwing keeps the dialog open with the sentence. */
  run: (assertion: string) => Promise<void>
  /** Turns a refusal from `run` into a sentence. */
  words: (error: unknown) => string
}

export function IrreversibleConfirm({
  open,
  onClose,
  title,
  children,
  confirmLabel,
  run,
  words,
}: IrreversibleConfirmProps) {
  const [method, setMethod] = useState<StepUpMethod>('totp')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const held = open ? heldAssertion() : null

  useEffect(() => {
    if (!open) {
      setCode('')
      setSent(false)
      setError(null)
    }
  }, [open])

  const sends = STEP_UP_CHOICES.some((c) => c.value === method && c.sends)

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      let assertion = held
      if (!assertion) {
        if (!code.trim()) {
          setError('Type the code from your two-step verification first.')
          return
        }
        assertion = await obtainStepUp(method, code.trim())
      }
      await run(assertion)
    } catch (e) {
      // A refused assertion is not kept: the next attempt asks for a fresh code.
      if (e instanceof ApiError && e.code.startsWith('STEPUP_')) forgetAssertion()
      setError(e instanceof StepUpError ? e.message : words(e))
    } finally {
      setBusy(false)
      setCode('')
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={title}
      confirmLabel={confirmLabel}
      cancelLabel="Not now"
      tone="danger"
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
    >
      {children}
      {held ? (
        <p>You verified yourself a moment ago; that still covers this step.</p>
      ) : (
        <div className="space-y-3">
          <p>This cannot be undone, so confirm it is you with your two-step verification.</p>
          <RadioGroup
            legend="Verify with"
            options={STEP_UP_CHOICES.map((c) => ({ value: c.value, label: c.label }))}
            value={method}
            onChange={(value) => {
              setMethod(value)
              setSent(false)
            }}
          />
          {sends && (
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => {
                setError(null)
                sendStepUpCode(method as 'email' | 'sms')
                  .then(() => setSent(true))
                  .catch((e) => setError(e instanceof StepUpError ? e.message : words(e)))
              }}
            >
              {sent ? 'Send the code again' : 'Send me a code'}
            </Button>
          )}
          <Input
            label="Verification code"
            value={code}
            inputMode={method === 'backup_code' ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
      )}
    </ConfirmDialog>
  )
}
